import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import Activate from "@/pages/Activate";
import Dashboard from "@/pages/Dashboard";
import Placements from "@/pages/Placements";
import OpenRequests from "@/pages/OpenRequests";
import PlacementDetail from "@/pages/PlacementDetail";
import Schools from "@/pages/Schools";
import SchoolImport from "@/pages/SchoolImport";
import Facilities from "@/pages/Facilities";
import Students from "@/pages/Students";
import StudentProfile from "@/pages/StudentProfile";
import ClinicalInstructors from "@/pages/ClinicalInstructors";
import Paperwork from "@/pages/Paperwork";
import Videos from "@/pages/Videos";
import Calendar from "@/pages/Calendar";
import Recruiting from "@/pages/Recruiting";
import Touchpoints from "@/pages/Touchpoints";
import Engagement from "@/pages/Engagement";
import Analytics from "@/pages/Analytics";
import Audit from "@/pages/Audit";
import Templates from "@/pages/Templates";
import Lists from "@/pages/Lists";
import StudentTasks from "@/pages/StudentTasks";
import Settings from "@/pages/Settings";
import Readiness from "@/pages/Readiness";
import Reports from "@/pages/Reports";
import Huddle from "@/pages/Huddle";
import Resources from "@/pages/Resources";
import Users from "@/pages/Users";
import HelpCenter from "@/pages/HelpCenter";
import Messages from "@/pages/Messages";
import Notifications from "@/pages/Notifications";
import MyBio from "@/pages/MyBio";
import BioView from "@/pages/BioView";
import GuestPage from "@/pages/GuestPage";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cc-blue" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role || user.role === "Guest") return <GuestPage />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/activate" element={<Activate />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/placements" element={<Placements />} />
        <Route path="/open-requests" element={<OpenRequests />} />
        <Route path="/placements/:id" element={<PlacementDetail />} />
        <Route path="/schools" element={<Schools />} />
        <Route path="/school-import" element={<SchoolImport />} />
        <Route path="/facilities" element={<Facilities />} />
        <Route path="/students" element={<Students />} />
        <Route path="/students/:id" element={<StudentProfile />} />
        <Route path="/clinical-instructors" element={<ClinicalInstructors />} />
        <Route path="/paperwork" element={<Paperwork />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/recruiting" element={<Recruiting />} />
        <Route path="/touchpoints" element={<Touchpoints />} />
        <Route path="/engagement" element={<Engagement />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/lists" element={<Lists />} />
        <Route path="/student-tasks" element={<StudentTasks />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/readiness" element={<Readiness />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/huddle" element={<Huddle />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/users" element={<Users />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/my-bio" element={<MyBio />} />
        <Route path="/bios/:userId" element={<BioView />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
